import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { convertDatesInObject } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import { isMemoryTargetReady, memoryKeys } from '@/hooks/memories/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'
import type { MemoryListResponse, MemoryTarget } from '@/types/Memory'

const MEMORY_DATE_KEYS = [
  'approved_at',
  'reviewed_at',
  'archived_at',
  'created_at',
  'last_modified',
]

const EMPTY_MEMORY_LIST_RESPONSE: MemoryListResponse = {
  count: 0,
  has_more: false,
  data: [],
  can_create_private: false,
  can_create_public: false,
  can_review: false,
}

export const getMemoryTargetEndpoint = (target: MemoryTarget): string => {
  switch (target.scope_kind) {
    case 'organization':
      return `${API_URL}/api/v1/organizations/${target.organization_id}/memories`
    case 'pursuit':
      return `${API_URL}/api/v1/pursuits/${target.pursuit_id}/memories`
    case 'file': {
      const url = new URL(`${API_URL}/api/v1/files/${target.file_id}/memories`)
      url.searchParams.set('organization_id', target.organization_id)
      if (target.pursuit_id) {
        url.searchParams.set('pursuit_id', target.pursuit_id)
      }
      return url.toString()
    }
  }
}

const MEMORY_PAGE_SIZE = 100

const getMemoryPageEndpoint = (
  target: MemoryTarget,
  limit: number,
  offset: number,
  { includeFiles = false }: { includeFiles?: boolean } = {},
): string => {
  const url = new URL(getMemoryTargetEndpoint(target))
  url.searchParams.set('limit', String(limit))
  url.searchParams.set('offset', String(offset))
  if (includeFiles && target.scope_kind !== 'file') {
    url.searchParams.set('include_files', 'true')
  }
  return url.toString()
}

export const parseMemoryResponseDates = <T>(payload: T): T => {
  return convertDatesInObject(payload, MEMORY_DATE_KEYS) as T
}

const readMemoryErrorDetail = async (
  response: Response,
): Promise<string | undefined> => {
  try {
    const payload = (await response.clone().json()) as { detail?: unknown }
    return typeof payload.detail === 'string' && payload.detail.trim()
      ? payload.detail.trim()
      : undefined
  } catch {
    return undefined
  }
}

export const buildMemoryFetchError = async (
  response: Response,
  message: string,
): Promise<SweetspotError> => {
  const detail = await readMemoryErrorDetail(response)
  return new SweetspotError({
    name: 'FETCH_ERROR',
    message: detail ? `${message}: ${detail}` : message,
    cause: detail
      ? { status: response.status, detail }
      : { status: response.status },
  })
}

const fetchMemoryPage = async (
  target: MemoryTarget,
  accessToken: string,
  {
    limit,
    offset,
    includeFiles,
  }: { limit: number; offset: number; includeFiles?: boolean },
): Promise<MemoryListResponse> => {
  const response = await fetch(
    getMemoryPageEndpoint(target, limit, offset, { includeFiles }),
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )

  if (!response.ok) {
    const error = await buildMemoryFetchError(
      response,
      'Failed to fetch memories',
    )
    throw error
  }

  return parseMemoryResponseDates(await response.json())
}

export const fetchMemories = async (
  target: MemoryTarget,
  accessToken: string | undefined | null,
  { includeFiles = false }: { includeFiles?: boolean } = {},
): Promise<MemoryListResponse> => {
  if (!accessToken || !isMemoryTargetReady(target)) {
    return EMPTY_MEMORY_LIST_RESPONSE
  }

  let offset = 0
  let firstPage: MemoryListResponse | undefined
  const data: MemoryListResponse['data'] = []

  while (true) {
    const page = await fetchMemoryPage(target, accessToken, {
      limit: MEMORY_PAGE_SIZE,
      offset,
      includeFiles,
    })

    firstPage ??= page
    data.push(...page.data)
    if (!page.has_more) {
      break
    }
    if (page.data.length === 0) {
      throw new SweetspotError({
        name: 'FETCH_ERROR',
        message: 'Failed to fetch memories: invalid pagination response',
      })
    }
    offset += page.data.length
  }

  return {
    ...(firstPage ?? EMPTY_MEMORY_LIST_RESPONSE),
    data,
    has_more: false,
  }
}

export const fetchMemoryCount = async (
  target: MemoryTarget,
  accessToken: string | undefined | null,
  { includeFiles = true }: { includeFiles?: boolean } = {},
): Promise<MemoryCount> => {
  if (!accessToken || !isMemoryTargetReady(target)) {
    return { count: 0 }
  }
  const page = await fetchMemoryPage(target, accessToken, {
    limit: 1,
    offset: 0,
    includeFiles,
  })
  return { count: page.count }
}

interface UseMemoriesArgs {
  target: MemoryTarget
  enabled?: boolean
  /**
   * Parent scopes only: also list this scope's file memories (org-file rows
   * for org targets, pursuit-file rows for pursuit targets).
   */
  includeFiles?: boolean
}

export const useMemories = ({
  target,
  enabled = true,
  includeFiles = false,
}: UseMemoriesArgs) => {
  const { accessToken } = useAuthInfoWithThrow()

  return useQuery({
    queryKey: includeFiles
      ? ([...memoryKeys.target(target), 'with-files'] as const)
      : memoryKeys.target(target),
    queryFn: () => fetchMemories(target, accessToken, { includeFiles }),
    enabled: enabled && !!accessToken && isMemoryTargetReady(target),
    staleTime: 1000 * 60,
  })
}

interface UseMemoryCountArgs {
  orgId: UUID | ''
}

interface MemoryCount {
  count: number
}

export const useMemoryCount = ({ orgId }: UseMemoryCountArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const target: MemoryTarget = {
    scope_kind: 'organization',
    organization_id: orgId,
  }

  return useQuery({
    queryKey: memoryKeys.count(orgId),
    queryFn: () => fetchMemoryCount(target, accessToken),
    enabled: !!orgId && !!accessToken,
    staleTime: 1000 * 60,
  })
}
